import { Container, Row, Col } from "react-bootstrap";
import Particle from "../Particle";
import Techstack from "./Techstack";
import Toolstack from "./Toolstack";
import { Helmet } from "react-helmet-async";

const services = [
  {
    title: "FinOps & Cost Optimization",
    items: [
      "Application-level cost attribution & tagging for $1.2M+ monthly AWS spend",
      "Cost Explorer, CUR, Cost Categories & Cost Optimization Hub",
      "Right-sizing, Compute Optimizer & workload scheduling",
      "Savings Plans & Reserved Instance procurement",
    ],
  },
  {
    title: "Cloud Infrastructure Engineering",
    items: [
      "AWS: EC2, RDS, S3, ECS, EKS, Lambda, VPC, ALB, CloudFront",
      "Azure: Virtual Machines, App Service, Blob Storage, VNet, AD",
      "Server-based & serverless production architectures",
      "Secure, scalable, audit-ready infrastructure lifecycle",
    ],
  },
  {
    title: "CI/CD & DevSecOps Automation",
    items: [
      "Jenkins, GitLab CI/CD, GitHub Actions & Azure DevOps",
      "SonarQube · Trivy · Gitleaks · AWS Secrets Manager",
      "Automated failure handling, rollback & alerting",
      "Repeatable, security-gated deployment workflows",
    ],
  },
  {
    title: "Infrastructure as Code",
    items: [
      "Reusable Terraform & Terragrunt modules",
      "AWS CloudFormation & Ansible playbooks",
      "Bash & Python automation scripting",
      "Version-controlled infra for auditability & rollback",
    ],
  },
  {
    title: "Observability & Monitoring",
    items: [
      "Prometheus + Grafana across Linux & Windows servers",
      "Node Exporter & Windows Exporter with Alertmanager",
      "AWS CloudWatch + SNS endpoint availability monitoring",
      "Datadog & CloudTrail for centralized visibility",
    ],
  },
  {
    title: "Cloud Security & Compliance",
    items: [
      "GuardDuty · Inspector · WAF · Shield · Security Hub",
      "Config · CloudTrail · AWS KMS",
      "IAM policies & permission boundaries",
      "Azure Key Vault for secrets management",
    ],
  },
  {
    title: "Databases & Data Platforms",
    items: [
      "PostgreSQL, MySQL, TimescaleDB",
      "AWS RDS & Aurora, Redshift",
      "Azure Cosmos DB",
      "Zero-downtime database migrations",
    ],
  },
  {
    title: "AI-Assisted DevOps",
    items: [
      "ChatGPT, Claude & GitHub Copilot in the dev workflow",
      "AWS Q Developer & AWS FinOps Agent",
      "GitHub MCP for AI-driven tooling",
      "AI-assisted infrastructure automation & troubleshooting",
    ],
  },
  {
    title: "Hybrid Cloud & Networking",
    items: [
      "Proxmox VE on-prem virtualisation",
      "Hybrid connectivity between on-prem and AWS",
      "Secure VPC design: public/private subnets, NAT, IGW",
      "Route 53 DNS management & traffic routing",
    ],
  },
  {
    title: "Web Servers & Operating Systems",
    items: [
      "Nginx, Apache, Tomcat, IIS",
      "Linux (Ubuntu, Amazon Linux) & Windows Server",
      "Reverse proxies, SSL/TLS & caching layers",
      "ALB + WAF with managed rule sets",
    ],
  },
  {
    title: "Version Control & Collaboration",
    items: [
      "Git, GitHub, GitLab & Bitbucket",
      "Self-hosted GitLab CE platform administration",
      "Branch-based CI/CD trigger workflows",
      "GitHub Codespaces for cloud-based development",
    ],
  },
];

function About() {
  return (
    <>
      <Helmet>
        <title>About | Vedant Gaidhane — Cloud Infrastructure Engineer</title>
        <meta
          name="description"
          content="Vedant Gaidhane — Cloud & DevOps Engineer specialising in AWS, Azure, DevSecOps, and FinOps & cloud cost optimization."
        />
      </Helmet>

      <div className="about-section">
        <Particle />
        <Container style={{ position: "relative", zIndex: 2 }}>

          {/* Hero */}
          <div className="about-hero">
            <div className="about-hero-name">Vedant Gaidhane</div>
            <div className="about-hero-title">
              Cloud &amp; DevOps Engineer · AWS · FinOps &amp; Cloud Cost Optimization
            </div>
            <p className="about-hero-desc">
              Cloud &amp; DevOps Engineer with 1.9+ years of hands-on experience building,
              managing, and optimizing AWS infrastructure across production and internal
              environments. Experienced in infrastructure provisioning, application deployment,
              automation, security, monitoring, troubleshooting, and improving day-to-day cloud
              operations — with a strong focus on FinOps and cloud cost optimization, identifying
              waste, improving utilization, and increasing cost visibility.
            </p>

            {/* Cert/highlight badges */}
            <div style={{ display: "flex", justifyContent: "center", gap: "0.6rem", flexWrap: "wrap", marginTop: "1.5rem" }}>
              {["AWS Cloud", "Azure", "FinOps", "DevSecOps", "Terraform", "Kubernetes"].map((b) => (
                <span
                  key={b}
                  style={{
                    background: "rgba(108,99,255,0.10)",
                    border: "1px solid rgba(108,99,255,0.22)",
                    color: "var(--primary)",
                    fontSize: "0.75rem", fontWeight: 600,
                    padding: "4px 12px", borderRadius: "100px",
                    letterSpacing: "0.3px",
                  }}
                >
                  {b}
                </span>
              ))}
            </div>
          </div>

          {/* What I Do */}
          <h3 className="what-i-do-title">
            What <span>I Do</span>
          </h3>
          <div className="services-grid">
            {services.map((s) => (
              <div key={s.title} className="service-card">
                <div className="service-card-title">{s.title}</div>
                <ul className="service-card-list">
                  {s.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Tech Stack */}
          <h3 className="stack-section-title">
            Tech <span>Stack</span>
          </h3>
          <Techstack />

          {/* Tools */}
          <h3 className="stack-section-title">
            Tools I <span>Use</span>
          </h3>
          <Toolstack />

          {/* Education */}
          <Row className="justify-content-center" style={{ marginBottom: "2rem" }}>
            <Col md={8}>
              <div
                style={{
                  background: "var(--bg-card)",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--r-lg)",
                  padding: "1.6rem 1.8rem",
                  display: "flex", gap: "1.2rem", alignItems: "flex-start",
                }}
              >
                <div
                  style={{
                    width: 46, height: 46, borderRadius: "10px", flexShrink: 0,
                    background: "rgba(108,99,255,0.12)",
                    border: "1px solid rgba(108,99,255,0.22)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "1.3rem",
                  }}
                >
                  🎓
                </div>
                <div>
                  <div style={{ fontWeight: 700, color: "var(--white)", marginBottom: 3 }}>
                    Bachelor of Technology – Electronics Engineering
                  </div>
                  <div style={{ color: "var(--primary)", fontSize: "0.88rem", marginBottom: 3 }}>
                    Yeshwantrao Chavan College of Engineering (YCCE), Nagpur
                  </div>
                  <div style={{ color: "var(--muted)", fontSize: "0.82rem" }}>
                    2020 – 2024 &nbsp;·&nbsp; CGPA: 7.76
                  </div>
                </div>
              </div>
            </Col>
          </Row>

        </Container>
      </div>
    </>
  );
}

export default About;
